import { CollectionServiceInterface } from '@/domain/services/collection.service.interface';
import {
  CollectionEntity,
  CollectionWithProducts,
  CollectionListResponse,
  CollectionProduct,
  SearchFilters,
} from '@/domain/entities/collection.entity';
import {
  createBffClient,
} from '@/lib/bff/create-storefront-client';
import type { CollectionDto, CollectionProduct as BffCollectionProduct } from '@template-project/storefront-bff-client';

function mapCollection(c: CollectionDto): CollectionEntity {
  return {
    id: c.id,
    title: c.title,
    description: c.description,
    slug: c.slug,
    type: c.type,
    status: c.status,
    imageUri: c.imageUri,
    sortOrder: c.sortOrder,
    showInMenu: c.showInMenu,
    menuPosition: c.menuPosition,
    productCount: c.productCount,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  };
}

function mapProduct(p: BffCollectionProduct): CollectionProduct {
  return {
    id: p.id,
    title: p.title,
    description: p.description,
    slug: p.slug,
    tags: p.tags,
    status: p.status,
    priceMin: p.priceMin,
    priceMax: p.priceMax,
    imageUri: p.imageUri,
    colorOptions: p.colorOptions,
    images: p.images,
  };
}

function toBffFilters(filters: SearchFilters = {}) {
  const { currency, country, ...rest } = filters;
  return {
    ...rest,
    ...(currency ? { currency, country } : {}),
  };
}

export class CollectionBffService implements CollectionServiceInterface {
  private client = createBffClient();

  async getCollections(page = 1, limit = 20): Promise<CollectionListResponse> {
    try {
      const res = await this.client.listCollections({ page, limit });
      return {
        collections: res.data.map(mapCollection),
        pagination: res.meta.pagination,
      };
    } catch {
      return { collections: [], pagination: { page, limit, total: 0, totalPages: 0 } };
    }
  }

  async getMenuCollections(): Promise<CollectionEntity[]> {
    try {
      const res = await this.client.getMenuCollections();
      return res.data.map(mapCollection);
    } catch {
      return [];
    }
  }

  async getCollectionBySlug(
    slug: string,
    filters: SearchFilters = {},
  ): Promise<CollectionWithProducts> {
    try {
      const res = await this.client.getCollection(slug, toBffFilters(filters));
      return {
        collection: mapCollection(res.data.collection),
        products: res.data.products.map(mapProduct),
        facets: res.data.facets,
        pagination: res.meta.pagination,
      };
    } catch {
      return this.emptyResult(slug, filters.page || 1, filters.limit || 20);
    }
  }

  async getCollectionById(
    id: string,
    filters: SearchFilters = {},
  ): Promise<CollectionWithProducts> {
    return this.getCollectionBySlug(id, filters);
  }

  async searchProducts(filters: SearchFilters): Promise<CollectionWithProducts> {
    try {
      const res = await this.client.searchProducts(toBffFilters(filters));
      return {
        collection: mapCollection(res.data.collection),
        products: res.data.products.map(mapProduct),
        facets: res.data.facets,
        pagination: res.meta.pagination,
      };
    } catch {
      return this.emptyResult('search', filters.page || 1, filters.limit || 40);
    }
  }

  private emptyResult(slug: string, page: number, limit: number): CollectionWithProducts {
    return {
      collection: {
        id: slug, title: slug, description: '', slug, type: 'manual', status: 'active',
        sortOrder: 'manual', productCount: 0, createdAt: '', updatedAt: '',
      },
      products: [],
      facets: {},
      pagination: { page, limit, total: 0, totalPages: 0 },
    };
  }
}
